/*
 * Gerenciamento de MikroTiks: cadastro, teste de conexao, confianca no
 * certificado e script de VPN.
 *
 * A senha existe apenas no formulario de envio — a API nunca a devolve, entao
 * nao ha como preencher o campo ao editar (por desenho).
 */
import { useEffect, useRef, useState } from 'react';
import { PERMISSIONS } from '@hotspot/shared';
import { ApiRequestError, apiList, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  formatUptime,
  type ConnectionTestDto,
  type MikrotikAccessModeValue,
  type MikrotikDto,
  type ProvisionResultDto,
  type VpnScriptDto,
} from '../lib/types';
import { Alert, Badge, Button, Card, Field, Spinner } from '../components/ui';
import { HotspotProvisionPanel } from './HotspotProvisionPanel';
import { PortalPanel } from './PortalPanel';

function StatusBadge({ mikrotik }: { mikrotik: MikrotikDto }) {
  const tons = { online: 'success', offline: 'danger', unknown: 'neutral' } as const;
  const rotulos = { online: 'online', offline: 'offline', unknown: 'nao verificado' } as const;

  return (
    <span title={mikrotik.lastError ?? undefined}>
      <Badge tone={tons[mikrotik.status]} dot>
        {rotulos[mikrotik.status]}
      </Badge>
    </span>
  );
}

const FORM_INICIAL = {
  name: '',
  unitId: '',
  accessMode: 'public_ip' as MikrotikAccessModeValue,
  host: '',
  port: '443',
  useTls: true,
  username: '',
  password: '',
  hotspotServerName: '',
  /** Prepara o HTTPS no equipamento logo apos cadastrar. */
  provisionarHttps: false,
  desabilitarReverseProxy: false,
};

/** Padroes coerentes com cada forma de acesso, aplicados ao trocar o modo. */
const PADROES_POR_MODO: Record<
  MikrotikAccessModeValue,
  { port: string; useTls: boolean; hostHint: string }
> = {
  public_ip: {
    port: '443',
    useTls: true,
    // O modo descreve o caminho, nao restringe a faixa: em laboratorio e comum
    // usar IP privado alcancavel diretamente.
    hostHint: 'Endereco alcancavel direto pelo servidor (IP publico, ou privado em laboratorio).',
  },
  vpn: {
    port: '80',
    useTls: false,
    hostHint: 'IP do equipamento dentro do tunel (ex.: 10.99.0.10).',
  },
};

export function MikrotiksPage() {
  const { can } = useAuth();
  const podeEscrever = can(PERMISSIONS.MIKROTIK_WRITE);
  const podeAgir = can(PERMISSIONS.MIKROTIK_ACTION);

  const [equipamentos, setEquipamentos] = useState<MikrotikDto[] | null>(null);
  const [unidades, setUnidades] = useState<{ id: number; name: string }[]>([]);
  const [erro, setErro] = useState<string | null>(null);

  const [form, setForm] = useState(FORM_INICIAL);
  const [formError, setFormError] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [testando, setTestando] = useState<number | null>(null);
  const [teste, setTeste] = useState<{ id: number; resultado: ConnectionTestDto } | null>(null);
  const [vpn, setVpn] = useState<{ nome: string; script: VpnScriptDto } | null>(null);
  const [provisionando, setProvisionando] = useState<number | null>(null);
  /** Equipamento cujo painel de hotspot esta aberto. */
  const [hotspotPara, setHotspotPara] = useState<MikrotikDto | null>(null);
  /** Equipamento com o painel do portal aberto (aparencia + envio). */
  const [portalPara, setPortalPara] = useState<MikrotikDto | null>(null);
  const [provisionamento, setProvisionamento] = useState<{
    nome: string;
    id: number;
    resultado: ProvisionResultDto;
  } | null>(null);
  /** O formulario de cadastro fica recolhido; abre pelo botao "+". */
  const [formAberto, setFormAberto] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  async function carregar() {
    setErro(null);
    try {
      const [lista, opcoes] = await Promise.all([
        apiList<MikrotikDto>('/admin/mikrotiks'),
        apiRequest<{ id: number; name: string }[]>('/admin/units/options').catch(() => []),
      ]);
      setEquipamentos(lista.data);
      setUnidades(opcoes);
    } catch (caught) {
      setEquipamentos([]);
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
    }
  }

  useEffect(() => {
    void carregar();
  }, []);

  async function criar(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSalvando(true);
    try {
      const criado = await apiRequest<MikrotikDto>('/admin/mikrotiks', {
        method: 'POST',
        body: {
          name: form.name,
          unitId: form.unitId ? Number(form.unitId) : null,
          accessMode: form.accessMode,
          host: form.host,
          // Ao provisionar, o cadastro nasce apontando para o HTTP de bootstrap;
          // a rota de provisionamento move para HTTPS ao concluir.
          port: form.provisionarHttps ? 80 : Number(form.port),
          useTls: form.provisionarHttps ? false : form.useTls,
          username: form.username,
          password: form.password,
          hotspotServerName: form.hotspotServerName || null,
        },
      });

      const deveProvisionar = form.provisionarHttps;
      const desabilitarProxy = form.desabilitarReverseProxy;

      setForm(FORM_INICIAL);
      setFormAberto(false);
      await carregar();

      if (deveProvisionar) {
        await provisionar(criado, {
          httpsPort: Number(form.port) || 443,
          disableConflictingReverseProxy: desabilitarProxy,
        });
      }
    } catch (caught) {
      setFormError(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao cadastrar');
    } finally {
      setSalvando(false);
    }
  }

  /**
   * Cria o certificado e publica o www-ssl no equipamento, pela REST API.
   * Nao e automatico no salvar: e uma acao declarada, porque altera a
   * configuracao de um equipamento em producao.
   */
  async function provisionar(
    mikrotik: MikrotikDto,
    opcoes: { httpsPort: number; disableConflictingReverseProxy: boolean },
  ) {
    setProvisionando(mikrotik.id);
    setProvisionamento(null);
    try {
      const resultado = await apiRequest<ProvisionResultDto>(
        `/admin/mikrotiks/${mikrotik.id}/provision-https`,
        {
          method: 'POST',
          body: {
            bootstrapPort: 80,
            httpsPort: opcoes.httpsPort,
            disableConflictingReverseProxy: opcoes.disableConflictingReverseProxy,
          },
        },
      );
      setProvisionamento({ nome: mikrotik.name, id: mikrotik.id, resultado });
      await carregar();
    } catch (caught) {
      setErro(
        caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao preparar o HTTPS',
      );
    } finally {
      setProvisionando(null);
    }
  }


  async function testar(mikrotik: MikrotikDto) {
    setTestando(mikrotik.id);
    setTeste(null);
    try {
      const resultado = await apiRequest<ConnectionTestDto>(
        `/admin/mikrotiks/${mikrotik.id}/test-connection`,
        { method: 'POST' },
      );
      setTeste({ id: mikrotik.id, resultado });
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha no teste');
    } finally {
      setTestando(null);
    }
  }

  async function confiarCertificado(id: number, fingerprint: string) {
    try {
      await apiRequest(`/admin/mikrotiks/${id}/trust-certificate`, {
        method: 'POST',
        body: { fingerprint },
      });
      setTeste(null);
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao confiar');
    }
  }

  async function gerarVpn(mikrotik: MikrotikDto) {
    try {
      const script = await apiRequest<VpnScriptDto>(
        `/admin/mikrotiks/${mikrotik.id}/vpn-script`,
        { method: 'POST' },
      );
      setVpn({ nome: mikrotik.name, script });
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao gerar script');
    }
  }

  async function remover(mikrotik: MikrotikDto) {
    if (!window.confirm(`Remover o equipamento "${mikrotik.name}"?`)) return;
    try {
      await apiRequest(`/admin/mikrotiks/${mikrotik.id}`, { method: 'DELETE' });
      await carregar();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao remover');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">MikroTiks</h1>
          <p className="text-sm text-slate-500">
            Equipamentos da frota, comunicacao por REST API (RouterOS 7.1+).
          </p>
        </div>

        {podeEscrever ? (
          <Button
            onClick={() => {
              setFormError(null);
              setFormAberto((aberto) => {
                // Ao abrir, leva a tela ate o formulario (ele fica abaixo da lista).
                if (!aberto) {
                  requestAnimationFrame(() =>
                    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
                  );
                }
                return !aberto;
              });
            }}
            aria-expanded={formAberto}
            aria-controls="form-novo-equipamento"
          >
            {formAberto ? 'Cancelar' : '+ Novo equipamento'}
          </Button>
        ) : null}
      </div>

      {erro ? <Alert>{erro}</Alert> : null}

      <Card title="Equipamentos">
        {equipamentos === null ? (
          <Spinner />
        ) : equipamentos.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum equipamento cadastrado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs tracking-wide text-slate-500 uppercase">
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">Endereco</th>
                  <th className="py-2 pr-4">Unidade</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">RouterOS</th>
                  <th className="py-2 pr-4">Uptime</th>
                  <th className="py-2 pr-4">TLS</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {equipamentos.map((mikrotik) => (
                  <tr key={mikrotik.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-4">
                      <span className="font-medium text-slate-800">{mikrotik.name}</span>
                      <span className="block font-mono text-xs text-slate-400">
                        /p/{mikrotik.portalSlug}
                      </span>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs text-slate-600">
                      {mikrotik.host}:{mikrotik.port}
                      <span className="mt-0.5 block font-sans text-slate-400">
                        {mikrotik.accessMode === 'vpn' ? 'via VPN' : 'IP publico'}
                        {mikrotik.accessMode === 'vpn' && mikrotik.vpnAddress
                          ? ` · ${mikrotik.vpnAddress}`
                          : ''}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-slate-600">{mikrotik.unitName ?? '—'}</td>
                    <td className="py-2 pr-4">
                      <StatusBadge mikrotik={mikrotik} />
                      {mikrotik.lastLatencyMs !== null ? (
                        /*
                         * Nao e ping. E o tempo que a API do RouterOS levou para
                         * responder as consultas da verificacao — dezenas ou
                         * centenas de milissegundos mesmo em rede rapida, porque
                         * o servico web do roteador e lento perto do ICMP. Sem
                         * dizer isso, o numero parece rede ruim e manda procurar
                         * problema onde nao ha.
                         */
                        <span
                          className="block text-xs text-slate-400 tabular-nums"
                          title="Tempo de resposta da API do RouterOS, não o ping. Inclui a consulta de versão e identidade do equipamento."
                        >
                          {mikrotik.lastLatencyMs} ms de resposta
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-slate-600">
                      {mikrotik.routerosVersion ?? '—'}
                      {mikrotik.boardName ? (
                        <span className="block text-xs text-slate-400">{mikrotik.boardName}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-4 text-slate-600 tabular-nums">
                      {formatUptime(mikrotik.uptimeSeconds)}
                    </td>
                    <td className="py-2 pr-4">
                      {!mikrotik.useTls ? (
                        <span className="text-xs text-slate-400">sem TLS</span>
                      ) : mikrotik.tlsTrusted ? (
                        <span
                          className="text-xs text-emerald-700"
                          title={mikrotik.tlsFingerprintPreview ?? ''}
                        >
                          confiado
                        </span>
                      ) : (
                        <span className="text-xs text-amber-700">nao confiado</span>
                      )}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {podeAgir ? (
                        <Button
                          variant="ghost"
                          onClick={() => void testar(mikrotik)}
                          disabled={testando === mikrotik.id}
                        >
                          {testando === mikrotik.id ? 'Testando...' : 'Testar'}
                        </Button>
                      ) : null}
                      {podeEscrever ? (
                        <>
                          {/* Preparar HTTPS: util quando o www-ssl ainda nao esta pronto */}
                          {mikrotik.accessMode === 'public_ip' && !mikrotik.tlsTrusted ? (
                            <Button
                              variant="ghost"
                              className="ml-2"
                              disabled={provisionando === mikrotik.id}
                              onClick={() =>
                                void provisionar(mikrotik, {
                                  httpsPort: 443,
                                  disableConflictingReverseProxy: false,
                                })
                              }
                              title="Cria o certificado e publica o www-ssl no equipamento"
                            >
                              {provisionando === mikrotik.id ? 'Preparando...' : 'Preparar HTTPS'}
                            </Button>
                          ) : null}

                          <Button
                            variant="ghost"
                            className="ml-2"
                            onClick={() => setHotspotPara(mikrotik)}
                            title="Cria bridge, DHCP, NAT e servidor de hotspot"
                          >
                            Hotspot
                          </Button>

                          <Button
                            variant="ghost"
                            className="ml-2"
                            onClick={() => setPortalPara(mikrotik)}
                            title="Excecoes deste equipamento e envio para o roteador"
                          >
                            Portal
                          </Button>

                          {/* Script de VPN so faz sentido para equipamento em modo VPN */}
                          {mikrotik.accessMode === 'vpn' ? (
                            <Button
                              variant="ghost"
                              className="ml-2"
                              onClick={() => void gerarVpn(mikrotik)}
                              title="Gerar configuracao WireGuard para colar no equipamento"
                            >
                              VPN
                            </Button>
                          ) : null}
                          <Button
                            variant="ghost"
                            className="ml-2"
                            onClick={() => void remover(mikrotik)}
                          >
                            Remover
                          </Button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Portal do equipamento: aparencia + envio para o roteador */}
      {portalPara ? (
        <PortalPanel
          mikrotik={portalPara}
          podeEscrever={podeEscrever}
          onClose={() => setPortalPara(null)}
        />
      ) : null}

      {/* Provisionamento do hotspot (bridge, DHCP, NAT, servidor) */}
      {hotspotPara ? (
        <HotspotProvisionPanel
          mikrotik={hotspotPara}
          podeEscrever={podeEscrever}
          onClose={() => setHotspotPara(null)}
          onProvisioned={() => void carregar()}
        />
      ) : null}

      {/* Resultado do provisionamento de HTTPS */}
      {provisionamento ? (
        <Card title={`Preparacao do HTTPS — ${provisionamento.nome}`}>
          <div className="space-y-3">
            {provisionamento.resultado.ok ? (
              <Alert tone="success">
                Equipamento preparado. O cadastro foi movido para a porta{' '}
                {provisionamento.resultado.httpsPort} com TLS.
              </Alert>
            ) : (
              <Alert>Algum passo falhou. Veja o detalhe abaixo.</Alert>
            )}

            <ol className="space-y-2 text-sm">
              {provisionamento.resultado.steps.map((passo) => (
                <li key={passo.step} className="flex gap-2">
                  <span
                    className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-medium ${
                      passo.status === 'done'
                        ? 'bg-emerald-100 text-emerald-800'
                        : passo.status === 'skipped'
                          ? 'bg-slate-100 text-slate-600'
                          : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {passo.status === 'done'
                      ? 'feito'
                      : passo.status === 'skipped'
                        ? 'ignorado'
                        : 'falhou'}
                  </span>
                  <span className="text-slate-700">
                    <span className="font-mono text-xs text-slate-500">{passo.step}</span>{' '}
                    {passo.detail}
                  </span>
                </li>
              ))}
            </ol>

            {provisionamento.resultado.fingerprint ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">
                  Confirme o certificado recem-criado
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  Compare com <code className="font-mono">/certificate/print detail</code> no
                  equipamento antes de confiar.
                </p>
                <code className="mt-2 block overflow-x-auto rounded bg-white p-2 font-mono text-xs">
                  {provisionamento.resultado.fingerprint}
                </code>
                <Button
                  className="mt-2"
                  onClick={() =>
                    void confiarCertificado(
                      provisionamento.id,
                      provisionamento.resultado.fingerprint as string,
                    ).then(() => setProvisionamento(null))
                  }
                >
                  Confiar neste certificado
                </Button>
              </div>
            ) : null}

            <Button variant="ghost" onClick={() => setProvisionamento(null)}>
              Fechar
            </Button>
          </div>
        </Card>
      ) : null}

      {/* Resultado do teste de conexao */}
      {teste ? (
        <Card title="Resultado do teste de conexao">
          <div className="space-y-3">
            {teste.resultado.ok ? (
              <Alert tone="success">
                Comunicação estabelecida. A API do RouterOS respondeu em{' '}
                {teste.resultado.latencyMs} ms — é o tempo do serviço web do roteador, bem
                maior que o ping, e não indica rede lenta.
              </Alert>
            ) : (
              <Alert>
                <span className="font-medium">{teste.resultado.errorMessage}</span>
                {teste.resultado.errorDetail ? (
                  <span className="mt-1 block font-mono text-xs opacity-80">
                    {teste.resultado.errorCode}: {teste.resultado.errorDetail}
                  </span>
                ) : null}
              </Alert>
            )}

            {teste.resultado.router ? (
              <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                <div>
                  <dt className="text-slate-500">Identidade</dt>
                  <dd className="font-medium">{teste.resultado.router.identity || '—'}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Versao</dt>
                  <dd className="font-medium">{teste.resultado.router.version}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Modelo</dt>
                  <dd className="font-medium">{teste.resultado.router.boardName}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Uptime</dt>
                  <dd className="font-medium tabular-nums">
                    {formatUptime(teste.resultado.router.uptimeSeconds)}
                  </dd>
                </div>
              </dl>
            ) : null}

            {teste.resultado.hotspotServers.length > 0 ? (
              <div className="text-sm">
                <p className="mb-1 text-slate-500">Servidores de hotspot no equipamento:</p>
                <ul className="list-inside list-disc text-slate-700">
                  {teste.resultado.hotspotServers.map((servidor) => (
                    <li key={servidor.id}>
                      <span className="font-mono">{servidor.name}</span>
                      {servidor.interface ? ` — ${servidor.interface}` : ''}
                      {servidor.disabled ? ' (desabilitado)' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {teste.resultado.observedFingerprint &&
            teste.resultado.requiresFingerprintConfirmation &&
            podeEscrever ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">
                  Confirme o certificado do equipamento
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  Compare com o fingerprint exibido no roteador antes de confiar. Depois de
                  gravado, uma troca de certificado passa a ser bloqueada.
                </p>
                <code className="mt-2 block overflow-x-auto rounded bg-white p-2 font-mono text-xs">
                  {teste.resultado.observedFingerprint}
                </code>
                <Button
                  className="mt-2"
                  onClick={() =>
                    void confiarCertificado(teste.id, teste.resultado.observedFingerprint as string)
                  }
                >
                  Confiar neste certificado
                </Button>
              </div>
            ) : null}

            <Button variant="ghost" onClick={() => setTeste(null)}>
              Fechar
            </Button>
          </div>
        </Card>
      ) : null}

      {/* Script de VPN */}
      {vpn ? (
        <Card title={`Configuracao WireGuard — ${vpn.nome}`}>
          <div className="space-y-3">
            <Alert tone="warning">
              A chave privada aparece uma unica vez e nao e armazenada. Copie o script agora; se
              precisar de outro, gere novamente (a chave anterior deixa de valer).
            </Alert>

            <div>
              <p className="mb-1 text-sm font-medium text-slate-700">
                1. Cole no terminal do MikroTik
              </p>
              <pre className="max-h-72 overflow-auto rounded-lg bg-slate-900 p-3 font-mono text-xs text-slate-100">
                {vpn.script.routerScript}
              </pre>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-slate-700">2. Execute no servidor</p>
              <pre className="overflow-auto rounded-lg bg-slate-900 p-3 font-mono text-xs text-slate-100">
                {vpn.script.serverPeerSnippet}
              </pre>
            </div>

            <p className="text-sm text-slate-600">
              Depois disso, altere o endereco do equipamento para{' '}
              <code className="font-mono">{vpn.script.tunnelAddress}</code> e teste a conexao.
            </p>

            <Button variant="ghost" onClick={() => setVpn(null)}>
              Fechar
            </Button>
          </div>
        </Card>
      ) : null}

      {podeEscrever && formAberto ? (
        <div ref={formRef} id="form-novo-equipamento">
          <Card title="Novo equipamento">
          <form onSubmit={criar} className="grid gap-4 sm:grid-cols-2">
            {formError ? (
              <div className="sm:col-span-2">
                <Alert>{formError}</Alert>
              </div>
            ) : null}

            <Field
              label="Nome"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              required
              minLength={2}
            />

            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Unidade</span>
              <select
                value={form.unitId}
                onChange={(event) => setForm({ ...form, unitId: event.target.value })}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
              >
                <option value="">Sem unidade</option>
                {unidades.map((unidade) => (
                  <option key={unidade.id} value={unidade.id}>
                    {unidade.name}
                  </option>
                ))}
              </select>
            </label>

            {/* Escolha explicita da forma de acesso: define os padroes abaixo */}
            <fieldset className="sm:col-span-2">
              <legend className="mb-2 text-sm font-medium text-slate-700">
                Como o servidor alcanca este equipamento
              </legend>

              <div className="grid gap-2 sm:grid-cols-2">
                {(
                  [
                    {
                      valor: 'public_ip' as const,
                      titulo: 'Direto (IP publico)',
                      descricao:
                        'O servidor alcanca o equipamento direto no endereco dele, sem tunel.',
                    },
                    {
                      valor: 'vpn' as const,
                      titulo: 'VPN',
                      descricao: 'O equipamento entra num tunel com o servidor. Funciona com CGNAT e IP dinamico.',
                    },
                  ] satisfies { valor: MikrotikAccessModeValue; titulo: string; descricao: string }[]
                ).map((opcao) => (
                  <label
                    key={opcao.valor}
                    className={`cursor-pointer rounded-lg border p-3 text-sm transition-colors ${
                      form.accessMode === opcao.valor
                        ? 'border-brand-500 bg-brand-50'
                        : 'border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <span className="flex items-center gap-2 font-medium text-slate-800">
                      <input
                        type="radio"
                        name="accessMode"
                        value={opcao.valor}
                        checked={form.accessMode === opcao.valor}
                        onChange={() =>
                          setForm({
                            ...form,
                            accessMode: opcao.valor,
                            port: PADROES_POR_MODO[opcao.valor].port,
                            useTls: PADROES_POR_MODO[opcao.valor].useTls,
                          })
                        }
                        className="size-4"
                      />
                      {opcao.titulo}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">{opcao.descricao}</span>
                  </label>
                ))}
              </div>

              <div className="mt-2">
                {form.accessMode === 'public_ip' ? (
                  <Alert tone="warning">
                    Expor a API do RouterOS na internet exige protecao no proprio equipamento:
                    regra de firewall liberando a porta apenas para o IP do servidor, usuario de
                    API dedicado e senha unica. Sem isso, o roteador fica sujeito a forca bruta.
                  </Alert>
                ) : (
                  <Alert tone="info">
                    Cadastre o equipamento e use o botao <strong>VPN</strong> na listagem para
                    gerar o script de configuracao. Depois, informe aqui o IP do tunel.
                  </Alert>
                )}
              </div>
            </fieldset>

            <Field
              label="Host (IP ou nome)"
              value={form.host}
              onChange={(event) => setForm({ ...form, host: event.target.value })}
              required
              hint={PADROES_POR_MODO[form.accessMode].hostHint}
            />

            <Field
              label="Porta"
              type="number"
              value={form.port}
              onChange={(event) => setForm({ ...form, port: event.target.value })}
              required
              min={1}
              max={65535}
              hint="443 com TLS (www-ssl) ou 80 sem TLS."
            />

            <Field
              label="Usuario da API"
              value={form.username}
              onChange={(event) => setForm({ ...form, username: event.target.value })}
              required
              autoComplete="off"
              hint="Use um usuario dedicado, com grupo restrito."
            />

            <Field
              label="Senha"
              type="password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              required
              autoComplete="new-password"
              hint="Guardada cifrada. A API nunca a devolve."
            />

            <Field
              label="Servidor de hotspot (opcional)"
              value={form.hotspotServerName}
              onChange={(event) => setForm({ ...form, hotspotServerName: event.target.value })}
              hint="Nome no RouterOS, ex.: hotspot1. O teste de conexao lista os disponiveis."
            />

            <label className="flex items-end gap-2 pb-1">
              <input
                type="checkbox"
                checked={form.useTls}
                disabled={form.provisionarHttps}
                onChange={(event) =>
                  setForm({
                    ...form,
                    useTls: event.target.checked,
                    port: event.target.checked ? '443' : '80',
                  })
                }
                className="size-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">
                Usar TLS (recomendado fora da VPN)
              </span>
            </label>

            {/* Provisionamento assistido do HTTPS no equipamento */}
            {form.accessMode === 'public_ip' ? (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 sm:col-span-2">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={form.provisionarHttps}
                    onChange={(event) =>
                      setForm({ ...form, provisionarHttps: event.target.checked })
                    }
                    className="mt-0.5 size-4 rounded border-slate-300"
                  />
                  <span className="text-sm">
                    <span className="font-medium text-slate-800">
                      Preparar o HTTPS no equipamento automaticamente
                    </span>
                    <span className="mt-1 block text-xs text-slate-600">
                      Cria e assina um certificado, atribui ao servico{' '}
                      <code className="font-mono">www-ssl</code> e publica na porta {form.port}.
                      Executado pela REST API do RouterOS — nao usa SSH.
                    </span>
                  </span>
                </label>

                {form.provisionarHttps ? (
                  <div className="mt-3 space-y-2 border-t border-slate-200 pt-3">
                    <Alert tone="warning">
                      A preparacao usa a porta 80 (HTTP) como caminho inicial, porque o HTTPS
                      ainda nao existe. Nesse momento a senha do roteador trafega em Basic auth.
                      Faca isso em rede confiavel — nao atraves da internet.
                    </Alert>

                    <p className="text-xs text-slate-600">
                      O usuario informado precisa ter permissao de escrita no RouterOS (grupo com
                      as policies <code className="font-mono">rest-api</code>,{' '}
                      <code className="font-mono">write</code> e{' '}
                      <code className="font-mono">policy</code> para mexer em certificados).
                    </p>

                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={form.desabilitarReverseProxy}
                        onChange={(event) =>
                          setForm({ ...form, desabilitarReverseProxy: event.target.checked })
                        }
                        className="mt-0.5 size-4 rounded border-slate-300"
                      />
                      <span className="text-xs text-slate-700">
                        Desabilitar o <code className="font-mono">reverse-proxy</code> se ele
                        estiver ativo na mesma porta. Deixe desmarcado se nao souber — o servico
                        pode estar em uso.
                      </span>
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Cadastrar equipamento'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setFormAberto(false)}>
                Cancelar
              </Button>
            </div>
          </form>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
